import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { PageHeader, Loading } from '../../components/office';
import { devMailboxApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Notice } from '@verlo/shared/components/ui/notice';
import { FileText } from 'lucide-react';

// Emails sent, on the dev stack only (`backend/dev_mailbox.py`): the order emails that would
// have gone to suppliers, kept here instead so they can be read exactly as sent. Anywhere
// else the API answers 404 and this says so.

const when = (iso) => new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export default function DevEmailsPage() {
  const [emails, setEmails] = useState(null);
  const [off, setOff] = useState(false);
  const [open, setOpen] = useState(null);

  const load = () => devMailboxApi.list()
    .then(({ data }) => { setEmails(data); setOpen((cur) => cur ?? data[0]?.id ?? null); })
    .catch((e) => { if (e?.response?.status === 404) setOff(true); else toast.error("Couldn't load the emails"); setEmails([]); });
  useEffect(() => { load(); }, []);

  const pdf = async (id, n) => {
    try {
      const { data } = await devMailboxApi.attachment(id, n);
      window.open(URL.createObjectURL(new Blob([data], { type: 'application/pdf' })), '_blank', 'noopener');
    } catch { toast.error("Couldn't open it"); }
  };

  if (!emails) return <Loading />;
  const shown = emails.find((e) => e.id === open);

  return (
    <div className="space-y-4" data-testid="dev-emails">
      <PageHeader title="Emails sent" purpose="This development copy of Verlo sends no email: orders to suppliers are kept here, as they'd arrive."
        actions={!off && <Button variant="outline" onClick={load}>Refresh</Button>} />
      {off ? (
        <Notice tone="info" word="Email">This page is only on a development copy of Verlo. Here, emails go to the people they&apos;re for.</Notice>
      ) : !emails.length ? (
        <Notice tone="info" word="Emails">Nothing yet. Send an order from Review and send and it lands here. The mailbox empties when the server restarts.</Notice>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
          <ul className="divide-y divide-hairline rounded-lg border border-hairline bg-white">
            {emails.map((e) => (
              <li key={e.id}>
                <button type="button" onClick={() => setOpen(e.id)} className={`block w-full px-4 py-3 text-left hover:bg-hover-tint ${e.id === open ? 'bg-verlo-tint' : ''}`} data-testid={`dev-email-${e.id}`}>
                  <span className="block truncate text-[14px] font-semibold text-ink">{e.subject}</span>
                  <span className="block truncate text-[12px] text-ink-faint">{e.to.join(', ')} · {when(e.at)}</span>
                </button>
              </li>
            ))}
          </ul>
          {shown && (
            <article className="min-w-0 rounded-lg border border-hairline bg-white p-5" data-testid="dev-email-open">
              <p className="text-[12.5px] text-ink-soft">To {shown.to.join(', ')}</p>
              <h2 className="mt-1 font-heading text-[18px] font-extrabold text-ink">{shown.subject}</h2>
              {/* The email's own HTML, built by the server from escaped values (`order_email`). */}
              <div className="mt-4 max-w-none overflow-x-auto text-[14px] leading-relaxed text-ink [&_p]:mb-3" dangerouslySetInnerHTML={{ __html: shown.html }} />
              {shown.attachments.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2 border-t border-hairline pt-3">
                  {shown.attachments.map((name, n) => (
                    <Button key={name} size="sm" variant="outline" onClick={() => pdf(shown.id, n)}><FileText size={14} className="mr-1.5" />{name}</Button>
                  ))}
                </div>
              )}
            </article>
          )}
        </div>
      )}
    </div>
  );
}
